import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  BrowserRouter,
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import {
  AnimatePresence,
  animate,
  motion,
  useInView,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from "motion/react";
import { ReactLenis, useLenis } from "lenis/react";
import "lenis/dist/lenis.css";
import {
  ArrowDown,
  ArrowRight,
  ArrowUpRight,
  BookOpenText,
  Briefcase,
  Buildings,
  CalendarBlank,
  CaretDown,
  ChartLineUp,
  ChatCircleText,
  Check,
  CheckCircle,
  Compass,
  DiscordLogo,
  EnvelopeSimple,
  Eye,
  Flag,
  Hammer,
  Handshake,
  Images,
  Info,
  InstagramLogo,
  Lightbulb,
  LinkedinLogo,
  List,
  MagnifyingGlass,
  MapTrifold,
  Megaphone,
  Path,
  Play,
  Plus,
  RocketLaunch,
  Scales,
  SealCheck,
  Sparkle,
  Student,
  Target,
  UsersThree,
  Wrench,
  X,
} from "@phosphor-icons/react";
import {
  aarohCollaborators,
  buildSteps,
  coreTeamGroups,
  fieldNotes,
  guides,
  impactAchievements,
  impactMetrics,
  opportunityPaths,
  peopleMet,
  problemCategories,
  programmes,
  stories,
  verifiedPartnerRelationships,
} from "./data/content.js";
import {
  findRoute,
  relatedRoutesFor,
  routeGroups,
  routeLabel,
  siteRoutes,
} from "./data/routes.js";
import {
  DEFAULT_SEO_IMAGE,
  MEDIA_LIBRARY,
  ORGANIZATION_LOGO,
  SITE_URL,
  getImageData,
  imageForRoute,
  organizationGraph,
} from "./data/seo.js";
import { AAROH_IMAGES } from "./data/images.js";
import { AAROH_ALBUM, AAROH_CLIPS } from "./data/album.js";
import { ReachMap } from "./components/ReachMap.jsx";
import { AURORA_COUNTRIES, Flag as CountryFlag } from "./components/flags.jsx";
import { SdgSection } from "./components/SdgWheel.jsx";
import { routes as aboutRoutes } from "./pages/about/index.jsx";
import { routes as programmeRoutes } from "./pages/programmes/index.jsx";
import { routes as learnRoutes } from "./pages/learn/index.jsx";
import { routes as storyRoutes } from "./pages/stories/index.jsx";
import { routes as joinRoutes } from "./pages/join/index.jsx";

const REDESIGNED_ROUTES = [...aboutRoutes, ...programmeRoutes, ...learnRoutes, ...storyRoutes, ...joinRoutes];

import {
  CAMPAIGN_IMAGES,
  IMAGEN_ASSETS,
  ThemeProvider,
  ensureMeta,
  buildBreadcrumbSchema,
  buildPageSchema,
  Seo,
  ScrollToTop,
  SmoothScrollRoot,
  NAV_GROUPS,
  JOIN_LINKS,
  BrandMark,
  useNavTone,
  isPathActive,
  NavLinkRow,
  NavPanel,
  SiteHeader,
  SiteFooter,
  PageFrame,
  revealPresets,
  Reveal,
  ImpactNumber,
  SectionLabel,
  TextLink,
  ProgressRail,
  Photo,
  EASE_OUT,
  useMediaQuery,
  MaskWords,
  ScrubWord,
  ScrubStatement,
  ClipPhoto,
} from "./kit.jsx";

const INTRO_KEY = "grid-intro-seen";
const INTRO_COLS = 5;
const INTRO_ROWS = 3;

function readIntroSeen() {
  try {
    return window.sessionStorage.getItem(INTRO_KEY) === "1";
  } catch {
    return true;
  }
}

function IntroSequence({ onDone }) {
  const reduceMotion = useReducedMotion();
  const [phase, setPhase] = useState("in");
  const finish = () => {
    try {
      window.sessionStorage.setItem(INTRO_KEY, "1");
    } catch {
      // Storage can be blocked; the intro then simply plays again next visit.
    }
    onDone();
  };

  useEffect(() => {
    if (reduceMotion) {
      finish();
      return undefined;
    }
    const out = window.setTimeout(() => setPhase("out"), 1500);
    const done = window.setTimeout(finish, 2450);
    const skip = () => {
      setPhase("out");
      window.setTimeout(finish, 700);
    };
    window.addEventListener("keydown", skip, { once: true });
    window.addEventListener("pointerdown", skip, { once: true });
    window.addEventListener("wheel", skip, { once: true, passive: true });
    return () => {
      window.clearTimeout(out);
      window.clearTimeout(done);
      window.removeEventListener("keydown", skip);
      window.removeEventListener("pointerdown", skip);
      window.removeEventListener("wheel", skip);
    };
  }, [reduceMotion]);

  if (reduceMotion) return null;
  const cells = Array.from({ length: INTRO_COLS * INTRO_ROWS }, (_, index) => {
    const col = index % INTRO_COLS;
    const row = Math.floor(index / INTRO_COLS);
    const distance = Math.hypot(col - (INTRO_COLS - 1) / 2, row - (INTRO_ROWS - 1) / 2);
    return { index, distance, center: col === 2 && row === 1 };
  });

  return (
    <div className="intro" aria-hidden="true">
      <div className="intro__cells">
        {cells.map((cell) => (
          <motion.span
            key={cell.index}
            className={`intro__cell ${cell.center ? "is-center" : ""}`}
            initial={{ scale: 1, borderRadius: 0 }}
            animate={phase === "out" ? { scale: 0, borderRadius: 48, opacity: 0.6 } : { scale: 1, borderRadius: 0 }}
            transition={{ duration: 0.7, delay: phase === "out" ? cell.distance * 0.07 : 0, ease: [0.65, 0, 0.35, 1] }}
          >
            {cell.center && (
              <motion.span
                className="intro__fill"
                initial={{ scale: 0, borderRadius: 999 }}
                animate={{ scale: 1, borderRadius: 28 }}
                transition={{ duration: 0.7, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
              />
            )}
          </motion.span>
        ))}
      </div>
      <motion.div
        className="intro__lines"
        initial={{ opacity: 1 }}
        animate={{ opacity: phase === "out" ? 0 : 1 }}
        transition={{ duration: 0.3 }}
      >
        {Array.from({ length: INTRO_COLS - 1 }, (_, i) => (
          <motion.span
            key={`v${i}`}
            className="intro__line intro__line--v"
            style={{ left: `${((i + 1) / INTRO_COLS) * 100}%` }}
            initial={{ scaleY: 0 }}
            animate={{ scaleY: 1 }}
            transition={{ duration: 0.8, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
          />
        ))}
        {Array.from({ length: INTRO_ROWS - 1 }, (_, i) => (
          <motion.span
            key={`h${i}`}
            className="intro__line intro__line--h"
            style={{ top: `${((i + 1) / INTRO_ROWS) * 100}%` }}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.1 + i * 0.06, ease: [0.16, 1, 0.3, 1] }}
          />
        ))}
      </motion.div>
      <motion.div
        className="intro__mark"
        initial={{ y: "60%", opacity: 0, clipPath: "inset(100% 0% 0% 0%)" }}
        animate={phase === "out"
          ? { y: "-40%", opacity: 0, clipPath: "inset(0% 0% 0% 0%)" }
          : { y: "0%", opacity: 1, clipPath: "inset(0% 0% 0% 0%)" }}
        transition={{ duration: phase === "out" ? 0.5 : 0.9, delay: phase === "out" ? 0 : 0.55, ease: [0.16, 1, 0.3, 1] }}
      >
        <img src="/images/brand/grid-mark.png" alt="" width="704" height="414" />
        <motion.span
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: phase === "out" ? 0 : 1, y: 0 }}
          transition={{ duration: 0.6, delay: phase === "out" ? 0 : 1.0 }}
        >
          Where ideas meet opportunities
        </motion.span>
      </motion.div>
    </div>
  );
}

const FILM = {
  loop: "/video/grid-story-muted-720.mp4",
  loopVertical: "/video/grid-story-vertical-muted-720.mp4",
  full: "/video/grid-story-720.mp4",
  fullVertical: "/video/grid-story-vertical-720.mp4",
  captions: "/video/grid-story.en.vtt",
  poster: "/video/grid-story-poster.jpg",
  posterVertical: "/video/grid-story-vertical-poster.jpg",
  length: "1:50",
};

// Chapter starts in the film (seconds), taken from the film's own schedule (design/story-film/narr.json).
const FILM_CHAPTERS = [
  ["GRID", 0],
  ["Aurora", 24.9],
  ["Metamorphosis", 37.7],
  ["AAROH", 49.6],
  ["Partners", 65.8],
  ["Team", 98.2],
];

const HERO_STATS = [
  ["2,013", "participants across our programmes"],
  ["27+", "countries reached by Aurora"],
  ["363", "young innovators at AAROH"],
  ["13", "teams recognised at AAROH"],
];

function usePortraitPhone() {
  const query = "(max-width: 700px) and (orientation: portrait)";
  const [match, setMatch] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setMatch(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  return match;
}

const HERO_REEL = {
  src: "/video/grid-hero-reel.mp4",
  portrait: "/video/grid-hero-reel-portrait.mp4",
  poster: "/video/grid-hero-reel.jpg",
};

const FILM_PLAY_EVENT = "grid:film-play";

// Hero: a full-bleed loop of real GRID photos with slow camera moves, the headline over it,
// and the numbers strip sliding up from the bottom edge.
function HeroCinema({ delay }) {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const portrait = usePortraitPhone();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const videoY = useTransform(scrollYProgress, (v) => `${Math.min(1, Math.max(0, v)) * 18}%`);
  const appear = (extra, y = 22) => ({
    initial: reduceMotion ? false : { opacity: 0, y },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.9, delay: delay + extra, ease: EASE_OUT },
  });
  function watch() {
    window.dispatchEvent(new Event(FILM_PLAY_EVENT));
    document.getElementById("film")?.scrollIntoView({ behavior: "smooth", block: "center" });
  }
  return (
    <section className="gh-cine" ref={ref} data-nav-tone="dark" data-nav-clear aria-labelledby="gh-hero-title">
      <motion.div className="gh-cine__media" style={reduceMotion ? undefined : { y: videoY }}>
        <motion.video
          key={portrait ? "p" : "l"}
          className="gh-cine__video"
          src={reduceMotion ? undefined : portrait ? HERO_REEL.portrait : HERO_REEL.src}
          poster={HERO_REEL.poster}
          autoPlay={!reduceMotion}
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
          initial={reduceMotion ? false : { scale: 1.14 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, delay, ease: EASE_OUT }}
        />
        <span className="gh-cine__shade" aria-hidden="true" />
      </motion.div>

      <div className="gh-cine__body">
        <motion.p className="gh-cine__meta" {...appear(0.1)}>Student-led · New Delhi · 27+ countries</motion.p>
        <MaskWords as="h1" id="gh-hero-title" className="gh-cine__title" text="Where ideas meet opportunities." onMount delay={delay + 0.2} />
        <div className="gh-cine__row">
          <motion.p {...appear(0.75)}>School students building real projects, together.</motion.p>
          <motion.div className="gh-actions" {...appear(0.85)}>
            <Link className="gh-btn gh-btn--fill" to="/get-involved">
              <span className="roll" data-label="Join GRID"><span>Join GRID</span></span>
            </Link>
            <button type="button" className="gh-btn gh-btn--ghost-dark gh-btn--button" onClick={watch}>
              <Play weight="fill" aria-hidden="true" />
              <span className="roll" data-label={`Watch the film · ${FILM.length}`}><span>Watch the film · {FILM.length}</span></span>
            </button>
          </motion.div>
        </div>
      </div>

      <motion.div
        className="gh-cine__strip"
        initial={reduceMotion ? false : { y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.1, delay: delay + 1.0, ease: EASE_OUT }}
      >
        <ul className="gh-cine__stats" aria-label="GRID in numbers">
          {HERO_STATS.map(([value, label]) => (
            <li key={label}>
              <strong><ImpactNumber value={value} /></strong>
              <span>{label}</span>
            </li>
          ))}
        </ul>
        <div className="gh-cine__flags" aria-label={`Students joined Aurora from ${AURORA_COUNTRIES.map((c) => c.name).join(", ")} and 12 more countries`}>
          <span className="gh-cine__flagrow" aria-hidden="true">
            {AURORA_COUNTRIES.map((country, index) => (
              <motion.span
                key={country.code}
                className="gh-cine__flag"
                initial={reduceMotion ? false : { scale: 0, y: 10 }}
                animate={{ scale: 1, y: 0 }}
                transition={{ duration: 0.5, delay: delay + 1.5 + index * 0.05, ease: EASE_OUT }}
              >
                <CountryFlag code={country.code} shape="round" />
              </motion.span>
            ))}
            <motion.span
              className="gh-cine__flag gh-cine__flag--more"
              initial={reduceMotion ? false : { scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: delay + 1.5 + AURORA_COUNTRIES.length * 0.05, ease: EASE_OUT }}
            >+12</motion.span>
          </span>
          <small>Students from 27+ countries</small>
        </div>
      </motion.div>
    </section>
  );
}

// The film: a big player (muted loop until you press play), the 9:16 cut in a phone beside it,
// and a row of chapter thumbnails that follow the film and jump to a chapter when pressed.
function FilmTheatre() {
  const videoRef = useRef(null);
  const phoneRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const vertical = usePortraitPhone();
  const [full, setFull] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(110);
  const chapter = FILM_CHAPTERS.reduce((current, [, start], index) => (time + 0.05 >= start ? index : current), 0);
  const chapterEnd = FILM_CHAPTERS[chapter + 1]?.[1] ?? duration;
  const chapterProgress = Math.min(1, Math.max(0, (time - FILM_CHAPTERS[chapter][1]) / (chapterEnd - FILM_CHAPTERS[chapter][1])));
  const src = full ? (vertical ? FILM.fullVertical : FILM.full) : (vertical ? FILM.loopVertical : FILM.loop);
  const poster = vertical ? FILM.posterVertical : FILM.poster;
  const phoneInView = useInView(phoneRef, { amount: 0.3 });

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    let frame = 0;
    const tick = () => { setTime(video.currentTime || 0); frame = requestAnimationFrame(tick); };
    const start = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(tick); };
    const stop = () => { cancelAnimationFrame(frame); setTime(video.currentTime || 0); };
    const meta = () => { if (Number.isFinite(video.duration)) setDuration(video.duration); };
    video.addEventListener("play", start);
    video.addEventListener("pause", stop);
    video.addEventListener("seeked", stop);
    video.addEventListener("loadedmetadata", meta);
    if (!video.paused) start();
    return () => {
      cancelAnimationFrame(frame);
      video.removeEventListener("play", start);
      video.removeEventListener("pause", stop);
      video.removeEventListener("seeked", stop);
      video.removeEventListener("loadedmetadata", meta);
    };
  }, [src]);

  useEffect(() => {
    const phone = phoneRef.current?.querySelector("video");
    if (!phone || reduceMotion) return;
    if (phoneInView) phone.play().catch(() => {}); else phone.pause();
  }, [phoneInView, reduceMotion]);

  function playFull(at = 0) {
    setFull(true);
    requestAnimationFrame(() => {
      const video = videoRef.current;
      if (!video) return;
      video.muted = false;
      video.loop = false;
      const go = () => { video.currentTime = at; video.play().catch(() => {}); };
      if (video.readyState >= 1) go(); else video.addEventListener("loadedmetadata", go, { once: true });
    });
  }

  useEffect(() => {
    const onPlay = () => playFull(0);
    window.addEventListener(FILM_PLAY_EVENT, onPlay);
    return () => window.removeEventListener(FILM_PLAY_EVENT, onPlay);
  });

  function jump(at) {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = at;
    setTime(at);
    if (video.paused && !reduceMotion) video.play().catch(() => {});
  }

  return (
    <section className="gh-theatre" aria-labelledby="gh-theatre-title">
      <MaskWords id="gh-theatre-title" className="gh-theatre__title" text="This is GRID." />
      <div className="gh-theatre__stage">
        <ClipFrame id="film" className={`gh-theatre__frame${vertical ? " is-vertical" : ""}${full ? " is-full" : ""}`}>
          <video
            key={src}
            ref={videoRef}
            className="gh-theatre__video"
            src={src}
            poster={poster}
            autoPlay={full || !reduceMotion}
            muted={!full}
            loop={!full}
            controls={full}
            playsInline
            preload={full ? "auto" : "metadata"}
            aria-label="The Project GRID film, with burned-in English captions"
          >
            <track kind="captions" src={FILM.captions} srcLang="en" label="English" />
          </video>
          {!full && (
            <button type="button" className="gh-film__play" onClick={() => playFull(0)}>
              <span className="gh-film__play-icon" aria-hidden="true"><Play weight="fill" /></span>
              <span><strong>Watch the GRID film</strong><small>{FILM.length} · sound on · captions</small></span>
            </button>
          )}
        </ClipFrame>
        {!vertical && (
          <figure className="gh-theatre__phone" ref={phoneRef}>
            <div className="gh-theatre__phone-body">
              <video src={FILM.loopVertical} poster={FILM.posterVertical} muted loop playsInline preload="none" aria-hidden="true" />
            </div>
            <figcaption>Vertical cut for Reels</figcaption>
          </figure>
        )}
      </div>
      <div className="gh-theatre__chapters" role="group" aria-label="Jump to a chapter of the film">
        {FILM_CHAPTERS.map(([label, at], index) => (
          <button key={label} type="button" className={`gh-chap${index === chapter ? " is-on" : ""}`} aria-pressed={index === chapter} onClick={() => jump(at)}>
            <span className="gh-chap__thumb"><img src={`/video/chapters/ch${index}.jpg`} alt="" loading="lazy" /></span>
            <span className="gh-chap__label">{label}</span>
            <span className="gh-chap__bar" aria-hidden="true">
              <span style={{ transform: `scaleX(${index === chapter ? chapterProgress : index < chapter ? 1 : 0})` }} />
            </span>
          </button>
        ))}
      </div>
      <p className="gh-film__credit">
        Music: "Voxel Revolution" by Kevin MacLeod (<a href="https://incompetech.com" target="_blank" rel="noreferrer">incompetech.com</a>), licensed under{" "}
        <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noreferrer">CC BY 4.0</a>. AI narration. Captions included.
      </p>
    </section>
  );
}

// A frame that opens with a clip-path reveal when it scrolls into view.
function ClipFrame({ id, className, children }) {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const inView = useInView(ref, { once: true, amount: 0.25 });
  return (
    <motion.div
      id={id}
      ref={ref}
      className={className}
      initial={reduceMotion ? false : { clipPath: "inset(8% 6% 8% 6% round 48px)" }}
      animate={inView || reduceMotion ? { clipPath: "inset(0% 0% 0% 0% round 28px)" } : undefined}
      transition={{ duration: 1.2, ease: EASE_OUT }}
    >
      {children}
    </motion.div>
  );
}


const albumPick = (id) => AAROH_ALBUM.find((photo) => photo.id === id);
const clipPick = (id) => AAROH_CLIPS.find((clip) => clip.id === id);
const aarohMoment = (id) => {
  const photo = albumPick(id);
  return { kind: "photo", src: photo.srcSmall, alt: photo.alt, place: "AAROH, IIIT Delhi", caption: photo.caption };
};
const aarohClip = (id) => {
  const clip = clipPick(id);
  return { kind: "video", src: clip.src, poster: clip.poster, alt: clip.alt, place: "AAROH, IIIT Delhi", caption: clip.caption, vertical: clip.height > clip.width };
};

const MOMENTS = [
  aarohMoment("M054"),
  aarohClip("M129"),
  aarohMoment("M102"),
  { kind: "photo", src: "/images/metamorphosis/album/meta-745-cohort-group-photo-800.webp", alt: "Metamorphosis students packing a lecture hall for a group photo at IIT Delhi", place: "Metamorphosis, IIT Delhi", caption: "The Metamorphosis cohort. GRID was the outreach partner." },
  { kind: "video", src: "/video/metamorphosis/meta-257-project-demo.mp4", poster: "/video/metamorphosis/meta-257-project-demo.jpg", alt: "A team demonstrates their project to two visitors at the Metamorphosis showcase", place: "Metamorphosis, IIT Delhi", caption: "A project demo at the showcase.", vertical: false },
  aarohMoment("M682"),
  aarohClip("M029"),
  aarohMoment("M609"),
  { kind: "photo", src: "/images/grid-photo-10-p8.jpg", alt: "Aurora overall winner Jerovin Floyd with his prize laptop", place: "Aurora", caption: "Overall winner Jerovin Floyd with his prize laptop." },
  aarohMoment("M533"),
  aarohClip("M443"),
  aarohMoment("M087"),
  { kind: "photo", src: "/images/metamorphosis/album/meta-510-wearable-prototype-test-800.webp", alt: "A student tries out a wearable prototype at the Metamorphosis project showcase", place: "Metamorphosis, IIT Delhi", caption: "Trying out a wearable prototype." },
  aarohMoment("M104"),
  aarohMoment("M241"),
];

function InViewVideo({ src, poster, alt, className = "" }) {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    const video = ref.current;
    if (!video || reduceMotion) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) video.play().catch(() => {});
      else video.pause();
    }, { threshold: 0.35 });
    observer.observe(video);
    return () => observer.disconnect();
  }, [reduceMotion]);
  return (
    <video ref={ref} className={className} poster={poster} muted loop playsInline preload="none" aria-label={alt}>
      {!reduceMotion && <source src={src} type="video/mp4" />}
    </video>
  );
}


function MomentsPan() {
  const ref = useRef(null);
  const trackRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const wide = useMediaQuery("(min-width: 900px)");
  const pinned = wide && !reduceMotion;
  const [distance, setDistance] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const x = useTransform(scrollYProgress, (value) => -Math.min(1, Math.max(0, value)) * distance);

  useEffect(() => {
    if (!pinned) return undefined;
    const measure = () => {
      const track = trackRef.current;
      if (track) setDistance(Math.max(0, track.scrollWidth - window.innerWidth + 48));
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (trackRef.current) observer.observe(trackRef.current);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [pinned]);

  return (
    <div className={`gh-moments ${pinned ? "is-pinned" : ""}`} ref={ref} style={pinned ? { height: `calc(100vh + ${distance}px)` } : undefined}>
      <div className="gh-moments__sticky">
        <motion.ul className="gh-moments__track" ref={trackRef} style={pinned ? { x } : undefined}>
          {MOMENTS.map((item, index) => (
            <li key={item.src} className={[item.vertical || index % 3 === 1 ? "is-tall" : index % 3 === 2 ? "is-low" : "", item.kind === "video" ? "is-video" : ""].join(" ").trim() || undefined}>
              <div className="gh-moments__img">
                {item.kind === "video"
                  ? <InViewVideo src={item.src} poster={item.poster} alt={`${item.place}: ${item.alt}`} />
                  : <img src={item.src} alt={`${item.place}: ${item.alt}`} loading="lazy" decoding="async" />}
                {item.kind === "video" && <span className="gh-moments__badge" aria-hidden="true"><Play weight="fill" /> Video</span>}
              </div>
              <p><strong>{item.place}</strong> {item.caption}</p>
            </li>
          ))}
        </motion.ul>
      </div>
    </div>
  );
}

function PhotoWall() {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const drift = [
    useTransform(scrollYProgress, (v) => `${-v * 22}%`),
    useTransform(scrollYProgress, (v) => `${-14 + v * 18}%`),
    useTransform(scrollYProgress, (v) => `${-v * 34}%`),
  ];
  const photos = AAROH_ALBUM.filter((photo) => photo.rank > 12).slice(0, 27);
  const columns = [0, 1, 2].map((col) => photos.filter((_, index) => index % 3 === col));
  return (
    <section className="gh-wall" ref={ref} aria-labelledby="gh-wall-title">
      <div className="gh-wall__head">
        <MaskWords id="gh-wall-title" text="Inside the rooms." />
      </div>
      <div className="gh-wall__cols">
        {columns.map((column, col) => (
          <motion.div className="gh-wall__col" key={col} style={reduceMotion ? undefined : { y: drift[col] }}>
            {column.map((photo) => (
              <figure key={photo.id}>
                <img src={photo.srcSmall} alt={photo.alt} width={photo.width} height={photo.height} loading="lazy" decoding="async" />
              </figure>
            ))}
          </motion.div>
        ))}
      </div>
      <div className="gh-wall__foot">
        <Link className="gh-btn gh-btn--ghost" to="/media"><span className="roll" data-label="See every photo"><span>See every photo</span></span></Link>
      </div>
    </section>
  );
}

const HOME_PROGRAMMES = [
  {
    slug: "aaroh",
    title: "AAROH",
    role: "Organised by GRID with Tachyon India and CiPD, IIIT Delhi",
    summary: "Two days at IIIT Delhi. Teams built across AI/ML, games, cybersecurity and hardware.",
    facts: [["Young innovators", "363"], ["Teams recognised", "13"], ["Where", "IIIT Delhi"]],
    image: AAROH_IMAGES.room,
    alt: "Students working in teams across the AAROH auditorium at IIIT Delhi",
    to: "/programmes/aaroh",
    cta: "Read the AAROH recap",
  },
  {
    slug: "aurora",
    title: "Aurora",
    role: "A Project GRID programme",
    summary: "An online hackathon for Grades 8 to 12 that works on slow internet.",
    facts: [["Participants", "1,500"], ["Countries", "27+"], ["Top teams", "Top 20 entered incubation"]],
    image: "/images/evidence/jerovin-chillgui-02.jpg",
    alt: "Aurora overall winner Jerovin Floyd Vincent Joseph holding his laptop prize",
    to: "/programmes/aurora",
    cta: "See Aurora's results",
  },
  {
    slug: "metamorphosis",
    title: "Metamorphosis",
    role: "GRID's role: outreach partner",
    summary: "Led by Tale of Humankind Foundation and IIT Delhi. GRID did outreach.",
    facts: [["Students", "150"], ["Organisers", "Tale of Humankind, IIT Delhi"], ["GRID's part", "Outreach"]],
    image: "/images/metamorphosis/album/meta-745-cohort-group-photo-1600.webp",
    alt: "Students gathered during Metamorphosis at IIT Delhi",
    to: "/programmes/metamorphosis",
    cta: "How we supported it",
  },
];

function ProgrammeStack() {
  return (
    <section className="gh-progs" id="programmes" aria-labelledby="gh-progs-title">
      <header className="gh-head">
        <MaskWords id="gh-progs-title" text="Programmes." />
      </header>
      <div className="gh-progs__stack">
        {HOME_PROGRAMMES.map((programme, index) => (
          <article className="gh-prog" key={programme.slug} style={{ top: `calc(92px + ${index * 22}px)` }}>
            <div className="gh-prog__copy">
              <span className="gh-pill">{programme.role}</span>
              <h3>{programme.title}</h3>
              <p>{programme.summary}</p>
              <dl>
                {programme.facts.map(([term, value]) => (
                  <div key={term}><dt>{term}</dt><dd>{value}</dd></div>
                ))}
              </dl>
              <Link className="gh-link" to={programme.to}>{programme.cta} <ArrowRight weight="bold" aria-hidden="true" /></Link>
            </div>
            <div className="gh-prog__media">
              <Photo src={programme.image} alt={programme.alt} sizes="(max-width: 900px) 100vw, 55vw" />
            </div>
          </article>
        ))}
      </div>
      <Link className="gh-btn gh-btn--ghost gh-progs__all" to="/programmes">
        <span className="roll" data-label="All programmes"><span>All programmes</span></span>
      </Link>
    </section>
  );
}

const HOME_STEPS = [
  ["Ask", "Find a real problem and the people who have it.", AAROH_IMAGES.studentDiscussion, "Students speaking during a discussion at AAROH"],
  ["Build", "Make a first version you can test.", AAROH_IMAGES.buildSession, "Students and a mentor working together around laptops at AAROH"],
  ["Show", "Present it. Learn what to fix next.", AAROH_IMAGES.studentPresentation, "A student presenting at a lectern at AAROH"],
];

function StepsStair() {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.8", "end 0.7"] });
  const pathLength = useTransform(scrollYProgress, [0, 0.85], [0, 1]);
  const headOpacity = useTransform(scrollYProgress, (latest) => Math.min(1, Math.max(0, (latest - 0.8) / 0.1)));
  return (
    <section className="gh-steps" aria-labelledby="gh-steps-title">
      <header className="gh-head">
        <MaskWords id="gh-steps-title" text="How it works." />
      </header>
      <div className="gh-steps__stage" ref={ref}>
        <svg className="gh-steps__line" viewBox="0 -30 1200 400" preserveAspectRatio="none" aria-hidden="true">
          <motion.path
            d="M 0 330 H 392 V 170 H 792 V 10 H 1150"
            style={reduceMotion ? undefined : { pathLength }}
            vectorEffect="non-scaling-stroke"
          />
          <motion.path
            className="gh-steps__head"
            d="M 1128 -12 L 1158 10 L 1128 32"
            style={reduceMotion ? undefined : { opacity: headOpacity }}
            vectorEffect="non-scaling-stroke"
          />
        </svg>
        <ol>
          {HOME_STEPS.map(([title, text, image, alt], index) => (
            <li key={title}>
              <span className="gh-steps__num">{String(index + 1).padStart(2, "0")}</span>
              <h3>{title}</h3>
              <p>{text}</p>
              <ClipPhoto src={image} alt={alt} sizes="(max-width: 900px) 100vw, 30vw" />
            </li>
          ))}
        </ol>
      </div>
      <Link className="gh-link" to="/guides">Start with a project guide <ArrowRight weight="bold" aria-hidden="true" /></Link>
    </section>
  );
}

function AarohPayoff() {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "start 0.15"] });
  const inset = useTransform(scrollYProgress, [0, 1], [28, 0]);
  const radius = useTransform(scrollYProgress, [0, 1], [40, 0]);
  const clipPath = useTransform([inset, radius], ([x, r]) => `inset(0px ${x}px round ${r}px)`);
  return (
    <section className="gh-aaroh" ref={ref} aria-labelledby="gh-aaroh-title">
      <motion.div className="gh-aaroh__panel" data-nav-tone="dark" style={reduceMotion ? undefined : { clipPath }}>
        <div className="gh-aaroh__intro">
          <span className="gh-mono">AAROH · IIIT Delhi</span>
          <MaskWords id="gh-aaroh-title" text="363 young innovators. Two days. One festival." accent="363" />
          <div className="gh-aaroh__text">
            <p>Run mostly by school students. Builders as young as 15. Thirteen teams recognised.</p>
            <ul aria-label="Innovation tracks">
              {["AI / ML", "Game development", "Cybersecurity", "Hardware"].map((track) => <li key={track}>{track}</li>)}
            </ul>
          </div>
        </div>
        
        <MomentsPan />
        <div className="gh-aaroh__links">
          <Link className="gh-btn gh-btn--fill" to="/programmes/aaroh"><span className="roll" data-label="Read the recap"><span>Read the recap</span></span></Link>
          <Link className="gh-btn gh-btn--ghost-dark" to="/media"><span className="roll" data-label="Photo gallery"><span>Photo gallery</span></span></Link>
        </div>
      </motion.div>
    </section>
  );
}

function PeopleRows() {
  const [founders, ...groups] = coreTeamGroups;
  return (
    <section className="gh-people" aria-labelledby="gh-people-title">
      <header className="gh-head">
        <MaskWords id="gh-people-title" text="The team." />
      </header>
      <ol className="gh-people__founders">
        {founders.members.map((member) => (
          <li key={member.name}>
            <span className="gh-people__initials" aria-hidden="true">{member.name.split(" ").map((part) => part[0]).join("")}</span>
            <strong>{member.name}</strong>
            <span>{member.role}</span>
          </li>
        ))}
      </ol>
      <div className="gh-people__groups">
        {groups.map((group) => (
          <div key={group.label}>
            <span className="gh-mono">{group.label}</span>
            <ul>
              {group.members.map((member) => (
                <li key={member.name}><strong>{member.name}</strong><span>{member.role}</span></li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <Link className="gh-link" to="/team">Meet the full team <ArrowRight weight="bold" aria-hidden="true" /></Link>
    </section>
  );
}

const PARTNER_GROUPS = [
  {
    programme: "Metamorphosis",
    note: "Organisers. GRID was the outreach partner.",
    logos: [
      ["/images/partners/tale-of-humankind.webp", "Tale of Humankind Foundation", "Organiser"],
      ["/images/partners/iit-delhi-aoni.png", "IIT Delhi, Office of Academic Outreach & New Initiatives", "Organiser"],
    ],
  },
  {
    programme: "AAROH",
    note: "Collaborators and listed supporters.",
    logos: [
      ["/images/partners/tachyon-india.png", "Tachyon India", "Collaborator"],
      ["/images/partners/cipd-iiitd.png", "CiPD, IIIT Delhi", "Institutional collaborator"],
      ["/images/partners/supporters/stacks.png", "Stacks KB", "Supporter"],
      ["/images/partners/supporters/fotocart.png", "Fotocart", "Supporter"],
      ["/images/partners/supporters/xyz.png", ".xyz", "Supporter"],
      ["/images/partners/supporters/github.png", "GitHub", "Supporter"],
      ["/images/partners/supporters/gradcircle.webp", "GradCircle", "Supporter"],
      ["/images/partners/supporters/skills4u.webp", "Skills4U", "Supporter"],
    ],
  },
  {
    programme: "Aurora",
    note: "Prizes.",
    logos: [["/images/partners/supporters/hp.png", "HP", "Prize laptops"]],
  },
];


function PartnersPanel() {
  const reduceMotion = useReducedMotion();
  return (
    <section className="gh-partners" aria-labelledby="gh-partners-title">
      <div className="gh-partners__panel">
        <header className="gh-head">
          <MaskWords id="gh-partners-title" text="Who we work with." />
        </header>
        <div className="gh-partners__groups">
          {PARTNER_GROUPS.map((group) => (
            <div className="gh-partners__group" key={group.programme}>
              <div className="gh-partners__label"><strong>{group.programme}</strong><span>{group.note}</span></div>
              <ul>
                {group.logos.map(([src, name, role], index) => (
                  <motion.li
                    key={name}
                    initial={reduceMotion ? false : { opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.7, delay: index * 0.05, ease: EASE_OUT }}
                  >
                    <span className="gh-partners__logo"><img src={src} alt={name} loading="lazy" /></span>
                    <span className="gh-partners__role">{role}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <Link className="gh-link" to="/partnerships">See every programme credit <ArrowRight weight="bold" aria-hidden="true" /></Link>
      </div>
    </section>
  );
}

const HOME_FAQ = [
  ["How can I join a programme?", "Upcoming events list dates, eligibility and how to apply.", "/programmes/upcoming", "Upcoming events"],
  ["Can my school work with GRID?", "Yes. Tell us your students' ages, group size and city.", "/for-schools", "For schools"],
  ["Who answers my questions?", "Email founder@projectgrid.org.", "/contact", "Contact"],
  ["Where are your policies?", "Governance, privacy and terms each have a page.", "/governance", "Governance"],
];

function HomeFaq() {
  return (
    <section className="gh-faq" aria-labelledby="gh-faq-title">
      <header className="gh-head">
        <MaskWords id="gh-faq-title" text="Questions." />
      </header>
      <div className="gh-faq__list">
        {HOME_FAQ.map(([question, answer, to, label]) => (
          <details key={question}>
            <summary>{question}<Plus weight="bold" aria-hidden="true" /></summary>
            <div>
              <p>{answer}</p>
              <Link className="gh-link" to={to}>{label} <ArrowRight weight="bold" aria-hidden="true" /></Link>
            </div>
          </details>
        ))}
      </div>
    </section>
  );
}

function NextSteps() {
  const routes = [
    ["I'm a student", "Join a programme or start a project.", "/for-students"],
    ["I represent a school", "Bring a GRID activity to your students.", "/for-schools"],
    ["I want to help", "Mentor, sponsor or join the team.", "/for-partners"],
  ];
  return (
    <section className="gh-next" aria-labelledby="gh-next-title">
      <MaskWords id="gh-next-title" text="Find your next step." />
      <ul>
        {routes.map(([title, text, to]) => (
          <li key={to}>
            <Link to={to}>
              <strong>{title}</strong>
              <span>{text}</span>
              <ArrowUpRight weight="bold" aria-hidden="true" />
            </Link>
          </li>
        ))}
      </ul>
      <p className="gh-next__mail">Not sure? Write to <a href="mailto:founder@projectgrid.org">founder@projectgrid.org</a></p>
    </section>
  );
}

function InstitutionHomePage() {
  const [introDone, setIntroDone] = useState(readIntroSeen);
  const [introDelay] = useState(() => (readIntroSeen() ? 0.1 : 1.85));
  const organizationSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@graph": organizationGraph(),
    }),
    [],
  );

  return (
    <PageFrame className="grid-home">
      <Seo schema={organizationSchema} />
      {!introDone && <IntroSequence onDone={() => setIntroDone(true)} />}
      <main>
        <HeroCinema delay={introDelay} />
        <FilmTheatre />
        <section className="gh-about" aria-label="About GRID">
          <ScrubStatement text="Built and run by school students. On every programme, we say exactly what our part was." />
        </section>
        <div id="goals"><SdgSection /></div>
        <PhotoWall />
        <ProgrammeStack />
        <StepsStair />
        <AarohPayoff />
        <PeopleRows />
        <ReachMap />
        <PartnersPanel />
        <HomeFaq />
        <NextSteps />
      </main>
    </PageFrame>
  );
}

function LegacyPeopleRedirect() {
  const { slug } = useParams();
  const isMeeting = peopleMet.some((item) => item.slug === slug);
  return <Navigate to={isMeeting ? `/meetings/${slug}` : "/team"} replace />;
}

function EdgeBlur() {
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    let frame = 0;
    const check = () => {
      frame = 0;
      const remaining = document.documentElement.scrollHeight - (window.scrollY + window.innerHeight);
      setHidden(remaining < 160);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(check);
    };
    check();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);
  return <div className={`edge-blur ${hidden ? "is-hidden" : ""}`} aria-hidden="true" />;
}

function AppRoutes() {
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  return (
    <>
      <ScrollToTop />
      <SiteHeader />
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          className="route-stage"
          key={location.pathname}
          initial={reduceMotion ? false : { opacity: 0, y: 14, filter: "blur(14px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)", transitionEnd: { filter: "none" } }}
          exit={reduceMotion ? undefined : { opacity: 0, y: -8, filter: "blur(12px)", transition: { duration: 0.32, ease: [0.65, 0, 0.35, 1] } }}
          transition={{ duration: reduceMotion ? 0 : 0.62, ease: [0.16, 1, 0.3, 1] }}
        >
          <Routes location={location}>
            <Route path="/" element={<InstitutionHomePage />} />
            {REDESIGNED_ROUTES.map(({ path, element }) => <Route key={path} path={path} element={element} />)}
            <Route path="/people" element={<Navigate to="/meetings" replace />} />
            <Route path="/people/:slug" element={<LegacyPeopleRedirect />} />
          </Routes>
        </motion.div>
      </AnimatePresence>
    </>
  );
}

export function App() {
  return (
    <ThemeProvider>
      <SmoothScrollRoot>
        <BrowserRouter>
          <AppRoutes />
          <EdgeBlur />
        </BrowserRouter>
      </SmoothScrollRoot>
    </ThemeProvider>
  );
}
